import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {isLazyOcticonName, LazyOcticon} from '@github-ui/lazy-octicon'
import {ActionList, ActionMenu, Label, Text, TextInput} from '@primer/react'
import {useId} from 'react'

import type {
  ProductAnnouncementModalDropdown as ProductAnnouncementModalDropdownData,
  ProductAnnouncementModalDropdownItem,
  ProductAnnouncementModalEvent,
  ProductAnnouncementModalEventPayload,
} from './../../../ProductAnnouncement/ProductAnnouncement.types'

import Styles from './ProductAnnouncementModalDropdown.module.css'

const ICON_SIZE = 20

const renderItemLabel = (item: ProductAnnouncementModalDropdownItem) => (
  <>
    {item.label}
    {item.isNew && (
      <>
        {' '}
        <Label aria-hidden="true" size="small" variant="success" className="ml-1">
          New
        </Label>
      </>
    )}
  </>
)

interface ProductAnnouncementModalDropdownProps {
  data: ProductAnnouncementModalDropdownData
  // When true, the trigger button stretches to fill its container's width.
  block?: boolean
  onEvent?: (event: ProductAnnouncementModalEvent, data?: ProductAnnouncementModalEventPayload) => void
}

const ProductAnnouncementModalDropdown = ({data, block, onEvent}: ProductAnnouncementModalDropdownProps) => {
  const inputId = useId()

  if (!data?.sections) return null

  const getItemIcon = (icon: ProductAnnouncementModalDropdownItem['icon']) => {
    if (!icon) return null

    if (typeof icon === 'string') {
      if (!isLazyOcticonName(icon)) return null
      // No `fallback={null}`: the trailing visual slot is sized by its content, so
      // LazyOcticon's placeholder keeps the item from reflowing when the chunk lands.
      return <LazyOcticon name={icon} size={ICON_SIZE} />
    }

    if (typeof icon === 'function') {
      const IconComponent = icon
      return <IconComponent size={ICON_SIZE} />
    }

    return (
      <img
        src={icon.src}
        alt={icon.alt ?? ''}
        width={ICON_SIZE}
        height={ICON_SIZE}
        className={Styles['ProductAnnouncementModalDropdown__item-icon']}
      />
    )
  }

  return (
    <ActionMenu
      onOpenChange={nextOpen => {
        if (nextOpen) {
          onEvent?.('tab_dropdown_opened', {
            label: data.label,
            ...(data.analyticsAction ? {analyticsAction: data.analyticsAction} : {}),
          })
        }
      }}
    >
      <ActionMenu.Button variant="primary" block={block}>
        {data.label}
      </ActionMenu.Button>

      <ActionMenu.Overlay width="medium" displayInViewport>
        <ActionList>
          {data.sections.map((section, index) => {
            if (section.type === 'divider') {
              return <ActionList.Divider key={`divider-${index}`} />
            }

            if (section.type === 'input') {
              const sectionInputId = `${inputId}-${index}`

              return (
                <div key={sectionInputId} className={Styles['ProductAnnouncementModalDropdown__input']}>
                  {section.label && (
                    <Text
                      as="label"
                      htmlFor={sectionInputId}
                      size="small"
                      className={Styles['ProductAnnouncementModalDropdown__input-label']}
                    >
                      {section.label}
                    </Text>
                  )}

                  <div className={Styles['ProductAnnouncementModalDropdown__input-content']}>
                    <TextInput
                      id={sectionInputId}
                      aria-label={section.label ?? data.label}
                      value={section.value}
                      readOnly
                      block
                    />
                    <CopyToClipboardIconButton
                      textToCopy={section.value}
                      ariaLabel={section.copyButtonLabel ?? ''}
                      onCopy={() =>
                        onEvent?.('tab_dropdown_input_copied', {
                          ...(section.label ? {label: section.label} : {}),
                          ...(section.analyticsAction ? {analyticsAction: section.analyticsAction} : {}),
                          value: section.value,
                        })
                      }
                    />
                  </div>
                </div>
              )
            }

            if (!section.items?.length) return null

            return (
              <ActionList.Group key={section.heading ?? section.items.map(item => item.id).join('-')}>
                {section.heading && <ActionList.GroupHeading>{section.heading}</ActionList.GroupHeading>}

                {section.items.map(item => {
                  const trailingVisual = getItemIcon(item.icon)

                  if (item.href) {
                    return (
                      <ActionList.LinkItem
                        key={item.id}
                        href={item.href}
                        target={item.isNewWindow ? '_blank' : undefined}
                        onClick={() =>
                          onEvent?.('tab_dropdown_item_clicked', {
                            dropdownItemId: item.id,
                            id: item.id,
                            label: item.label,
                            ...(item.analyticsAction ? {analyticsAction: item.analyticsAction} : {}),
                            url: item.href,
                          })
                        }
                      >
                        {renderItemLabel(item)}
                        {trailingVisual && <ActionList.TrailingVisual>{trailingVisual}</ActionList.TrailingVisual>}
                      </ActionList.LinkItem>
                    )
                  }

                  return (
                    <ActionList.Item
                      key={item.id}
                      onSelect={() =>
                        onEvent?.('tab_dropdown_item_clicked', {
                          dropdownItemId: item.id,
                          id: item.id,
                          label: item.label,
                          ...(item.analyticsAction ? {analyticsAction: item.analyticsAction} : {}),
                        })
                      }
                    >
                      {renderItemLabel(item)}
                      {trailingVisual && <ActionList.TrailingVisual>{trailingVisual}</ActionList.TrailingVisual>}
                    </ActionList.Item>
                  )
                })}
              </ActionList.Group>
            )
          })}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export default ProductAnnouncementModalDropdown
